// Éléments communs aux marchés clients (BC) et aux programmes de livraison (ADL).
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, LinearProgress, MenuItem, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { clientAPI, marcheAPI, stockAPI } from '../../services/api';
import { apiError } from '../../utils/format';
import { tokens } from '../../theme';

/** « 9AR150 » → « 9 m AR 150 », « 12B1250 » → « 12 m B 1250 » (désignation SENELEC). */
export const typeLabel = (code) => {
  const m = String(code || '').match(/^(\d+)(AR|B)(\d+)$/);
  return m ? `${m[1]} m ${m[2]} ${m[3]}` : code || '—';
};

export const MARCHE_STATUT = {
  OUVERT: { label: 'En cours', color: 'info' },
  SOLDE: { label: 'Soldé', color: 'success' },
  ANNULE: { label: 'Annulé', color: 'default' },
};

export const Avancement = ({ value, width = 120 }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: width }}>
    <LinearProgress variant="determinate" value={Math.min(100, value || 0)} sx={{ flex: 1, height: 7, borderRadius: 4, bgcolor: tokens.lineSoft,
      '& .MuiLinearProgress-bar': { bgcolor: value >= 100 ? '#1B7A46' : tokens.navy, borderRadius: 4 } }} />
    <Typography sx={{ fontSize: 12.5, fontFamily: tokens.mono, width: 38, textAlign: 'right' }}>{value || 0}%</Typography>
  </Box>
);

const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

/** Création ou modification d'un ADL : numéro, période, quantités à livrer par type. */
export function AdlDialog({ open, onClose, adl, onSaved }) {
  const [clients, setClients] = useState([]);
  const [types, setTypes] = useState([]);
  const [f, setF] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setError('');
    Promise.all([clientAPI.list(), stockAPI.getPbaTypes()]).then(([c, t]) => {
      setClients(c.data); setTypes(t.data);
      const senelec = c.data.find((x) => /senelec/i.test(x.nom));
      const now = new Date();
      setF(adl ? {
        client_id: adl.client_id, numero: adl.numero, annee: adl.annee, periode: adl.periode || '', date_adl: adl.date_adl || '',
        statut: adl.statut, notes: adl.notes || '', q: Object.fromEntries(adl.lignes.filter((l) => !l.hors_programme).map((l) => [l.pba_type, String(l.a_livrer)])),
      } : {
        client_id: senelec?.id || '', numero: '', annee: now.getFullYear(), periode: `${MOIS[now.getMonth()]} ${now.getFullYear()}`,
        date_adl: now.toISOString().slice(0, 10), statut: 'OUVERT', notes: '', q: {},
      });
    }).catch((e) => setError(apiError(e)));
  }, [open, adl]);
  const save = async () => {
    setBusy(true); setError('');
    try {
      const body = { ...f, lignes: Object.entries(f.q).filter(([, v]) => String(v).trim() !== '' && Number(v) > 0).map(([pba_type, quantite]) => ({ pba_type, quantite })) };
      const r = adl ? await marcheAPI.majAdl(adl.id, body) : await marcheAPI.creerAdl(body);
      onSaved(r.data);
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };
  const total = f ? Object.values(f.q).reduce((s, v) => s + (Number(v) || 0), 0) : 0;
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{adl ? `Modifier l'${adl.libelle}` : 'Nouveau programme de livraison (ADL)'}</DialogTitle>
      {f && (
        <DialogContent sx={{ pt: '8px !important' }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Client" value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })}>
                {clients.map((c) => <MenuItem key={c.id} value={c.id}>{c.nom}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="N° ADL" value={f.numero} inputProps={{ inputMode: 'numeric' }} onChange={(e) => setF({ ...f, numero: e.target.value })} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Année" value={f.annee} inputProps={{ inputMode: 'numeric' }} onChange={(e) => setF({ ...f, annee: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Période" placeholder="ex. Octobre 2026" value={f.periode} onChange={(e) => setF({ ...f, periode: e.target.value })} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth size="small" type="date" label="Date" InputLabelProps={{ shrink: true }} value={f.date_adl} onChange={(e) => setF({ ...f, date_adl: e.target.value })} /></Grid>
            <Grid item xs={6} sm={3}>
              <TextField select fullWidth size="small" label="État" value={f.statut} onChange={(e) => setF({ ...f, statut: e.target.value })}>
                <MenuItem value="OUVERT">En cours</MenuItem><MenuItem value="CLOS">Clos</MenuItem>
              </TextField>
            </Grid>
          </Grid>
          <Typography sx={{ fontWeight: 600, mt: 2, mb: 0.5 }}>Quantités à livrer</Typography>
          <Table size="small">
            <TableHead><TableRow><TableCell>Type de support</TableCell><TableCell align="right" sx={{ width: 140 }}>Quantité</TableCell></TableRow></TableHead>
            <TableBody>
              {types.map((t) => (
                <TableRow key={t.code}>
                  <TableCell>{typeLabel(t.code)}</TableCell>
                  <TableCell align="right">
                    <TextField variant="standard" value={f.q[t.code] || ''} inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' }, 'aria-label': `À livrer ${t.code}` }}
                      onChange={(e) => setF({ ...f, q: { ...f.q, [t.code]: e.target.value.replace(/[^\d]/g, '') } })} />
                  </TableCell>
                </TableRow>
              ))}
              <TableRow><TableCell sx={{ fontWeight: 700 }}>Total</TableCell><TableCell align="right" sx={{ fontWeight: 700, fontFamily: tokens.mono }}>{total}</TableCell></TableRow>
            </TableBody>
          </Table>
          <TextField fullWidth multiline minRows={2} size="small" label="Commentaire" sx={{ mt: 2 }} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" disabled={busy || !f || !f.client_id || !f.numero || !total} onClick={save}>Enregistrer</Button>
      </DialogActions>
    </Dialog>
  );
}
